import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { listingsApi, getFileUrl } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { getCrownVicImage, handleImageError } from '../lib/images';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { formatApiError } from '../lib/api';
import { Car, DollarSign, MapPin, Calendar, Search, Plus, Filter, Gauge, ImagePlus, X, Trash2 } from 'lucide-react';

export default function MarketplacePage() {
  const { user } = useAuth();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    min_price: '',
    max_price: '',
    year_min: '',
    year_max: '',
    location: '',
  });
  const [showFilters, setShowFilters] = useState(false);
  const [isNewListingOpen, setIsNewListingOpen] = useState(false);
  const [newListing, setNewListing] = useState({
    title: '',
    description: '',
    price: '',
    year: '',
    mileage: '',
    location: '',
    contact_email: '',
    contact_phone: '',
    features: '',
  });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [newImages, setNewImages] = useState([]); // File[] selected before submit
  const [uploadProgress, setUploadProgress] = useState(null); // { done, total }

  const MAX_IMAGES = 10;

  const handleImagesSelected = (e) => {
    const files = Array.from(e.target.files || []);
    const combined = [...newImages, ...files].slice(0, MAX_IMAGES);
    setNewImages(combined);
    e.target.value = ''; // allow re-picking same file
  };

  const removeImageAt = (idx) => {
    setNewImages((prev) => prev.filter((_, i) => i !== idx));
  };

  const fetchListings = async () => {
    try {
      const response = await listingsApi.getAll(filters);
      setListings(response.data);
    } catch (error) {
      console.error('Error fetching listings:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchListings();
  }, []);

  const handleApplyFilters = () => {
    setLoading(true);
    fetchListings();
    setShowFilters(false);
  };

  const handleClearFilters = () => {
    setFilters({
      min_price: '',
      max_price: '',
      year_min: '',
      year_max: '',
      location: '',
    });
    setLoading(true);
    fetchListings();
  };

  const handleCreateListing = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      const listingData = {
        title: newListing.title,
        description: newListing.description,
        price: parseFloat(newListing.price),
        year: parseInt(newListing.year),
        mileage: newListing.mileage ? parseInt(newListing.mileage) : null,
        location: newListing.location,
        contact_email: newListing.contact_email,
        contact_phone: newListing.contact_phone || null,
        features: newListing.features ? newListing.features.split(',').map(f => f.trim()) : [],
      };

      const createdRes = await listingsApi.create(listingData);
      const listingId = createdRes.data?.id;

      // Upload all selected images sequentially (with progress)
      if (listingId && newImages.length > 0) {
        setUploadProgress({ done: 0, total: newImages.length });
        for (let i = 0; i < newImages.length; i += 1) {
          try {
            await listingsApi.uploadImage(listingId, newImages[i]);
          } catch (imgErr) {
            console.error('Image upload failed', imgErr);
          }
          setUploadProgress({ done: i + 1, total: newImages.length });
        }
      }

      setIsNewListingOpen(false);
      setNewListing({
        title: '',
        description: '',
        price: '',
        year: '',
        mileage: '',
        location: '',
        contact_email: '',
        contact_phone: '',
        features: '',
      });
      setNewImages([]);
      setUploadProgress(null);
      fetchListings();
    } catch (error) {
      setError(formatApiError(error));
    } finally {
      setSubmitting(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(price);
  };

  const formatMileage = (mileage) => {
    if (!mileage) return 'N/A';
    return new Intl.NumberFormat('en-US').format(mileage) + ' mi';
  };

  const handleDeleteListing = async (e, listing) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm(`Delete "${listing.title}"? This cannot be undone.`)) return;
    try {
      await listingsApi.delete(listing.id);
      setListings((prev) => prev.filter((l) => l.id !== listing.id));
    } catch (err) {
      window.alert(`Failed to delete: ${formatApiError(err)}`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#FF3B30] border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-400 tag">Loading listings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Header */}
      <div className="bg-[#141414] border-b border-[#262626]">
        <div className="container mx-auto px-4 py-12">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <p className="tag text-[#FF3B30] mb-2">Marketplace</p>
              <h1 className="font-heading font-bold text-4xl md:text-5xl text-white mb-4">Crown Vics For Sale</h1>
              <p className="text-gray-400 max-w-2xl">
                Find your next race car. Browse Crown Victorias for sale from racers across the United States.
              </p>
            </div>
            {user ? (
              <Dialog open={isNewListingOpen} onOpenChange={setIsNewListingOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-[#FF3B30] hover:bg-[#E6352B] text-white" data-testid="new-listing-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    List Your Car
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-[#141414] border-[#262626] text-white max-w-lg max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="font-heading text-xl">List Your Crown Vic</DialogTitle>
                    <DialogDescription className="text-gray-400">
                      Post your Crown Vic race car for sale. Include mileage, price, contact info, and up to 10 photos.
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleCreateListing} className="space-y-4">
                    {error && <p className="text-red-500 text-sm">{error}</p>}
                    
                    <div>
                      <Label>Title *</Label>
                      <Input
                        value={newListing.title}
                        onChange={(e) => setNewListing({ ...newListing, title: e.target.value })}
                        placeholder="e.g., 2008 Crown Vic Race Car - Ready to Race"
                        className="bg-[#1F1F1F] border-[#262626] text-white"
                        required
                        data-testid="listing-title-input"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Year *</Label>
                        <Input
                          type="number"
                          value={newListing.year}
                          onChange={(e) => setNewListing({ ...newListing, year: e.target.value })}
                          placeholder="2008"
                          className="bg-[#1F1F1F] border-[#262626] text-white"
                          required
                          data-testid="listing-year-input"
                        />
                      </div>
                      <div>
                        <Label>Price *</Label>
                        <Input
                          type="number"
                          value={newListing.price}
                          onChange={(e) => setNewListing({ ...newListing, price: e.target.value })}
                          placeholder="5000"
                          className="bg-[#1F1F1F] border-[#262626] text-white"
                          required
                          data-testid="listing-price-input"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Mileage</Label>
                        <Input
                          type="number"
                          value={newListing.mileage}
                          onChange={(e) => setNewListing({ ...newListing, mileage: e.target.value })}
                          placeholder="120000"
                          className="bg-[#1F1F1F] border-[#262626] text-white"
                          data-testid="listing-mileage-input"
                        />
                      </div>
                      <div>
                        <Label>Location *</Label>
                        <Input
                          value={newListing.location}
                          onChange={(e) => setNewListing({ ...newListing, location: e.target.value })}
                          placeholder="Ohio, USA"
                          className="bg-[#1F1F1F] border-[#262626] text-white"
                          required
                          data-testid="listing-location-input"
                        />
                      </div>
                    </div>

                    <div>
                      <Label>Description *</Label>
                      <Textarea
                        value={newListing.description}
                        onChange={(e) => setNewListing({ ...newListing, description: e.target.value })}
                        placeholder="Describe your car, modifications, race history, etc."
                        rows={4}
                        className="bg-[#1F1F1F] border-[#262626] text-white"
                        required
                        data-testid="listing-description-input"
                      />
                    </div>

                    <div>
                      <Label>Features (comma separated)</Label>
                      <Input
                        value={newListing.features}
                        onChange={(e) => setNewListing({ ...newListing, features: e.target.value })}
                        placeholder="Roll cage, Racing seat, Fire system"
                        className="bg-[#1F1F1F] border-[#262626] text-white"
                        data-testid="listing-features-input"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Contact Email *</Label>
                        <Input
                          type="email"
                          value={newListing.contact_email}
                          onChange={(e) => setNewListing({ ...newListing, contact_email: e.target.value })}
                          placeholder="your@email.com"
                          className="bg-[#1F1F1F] border-[#262626] text-white"
                          required
                          data-testid="listing-email-input"
                        />
                      </div>
                      <div>
                        <Label>Phone (optional)</Label>
                        <Input
                          value={newListing.contact_phone}
                          onChange={(e) => setNewListing({ ...newListing, contact_phone: e.target.value })}
                          placeholder="555-123-4567"
                          className="bg-[#1F1F1F] border-[#262626] text-white"
                          data-testid="listing-phone-input"
                        />
                      </div>
                    </div>

                    <div>
                      <Label>Photos (up to {MAX_IMAGES}, at least 5 recommended)</Label>
                      <div className="mt-2 grid grid-cols-3 gap-2" data-testid="listing-photo-grid">
                        {newImages.map((f, idx) => (
                          <div key={idx} className="relative group aspect-square bg-[#1F1F1F] border border-[#262626] rounded overflow-hidden">
                            <img
                              src={URL.createObjectURL(f)}
                              alt=""
                              className="w-full h-full object-cover"
                            />
                            <button
                              type="button"
                              onClick={() => removeImageAt(idx)}
                              className="absolute top-1 right-1 w-6 h-6 bg-black/70 hover:bg-red-600 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                              aria-label={`Remove photo ${idx + 1}`}
                              data-testid={`remove-new-image-${idx}`}
                            >
                              <X className="w-3.5 h-3.5 text-white" />
                            </button>
                            {idx === 0 && (
                              <span className="absolute bottom-1 left-1 bg-[#FF3B30] text-white text-[10px] font-bold px-1.5 py-0.5 rounded uppercase">Main</span>
                            )}
                          </div>
                        ))}
                        {newImages.length < MAX_IMAGES && (
                          <label
                            className="aspect-square border-2 border-dashed border-[#262626] hover:border-[#FF3B30] rounded flex flex-col items-center justify-center text-gray-500 hover:text-[#FF3B30] cursor-pointer transition-colors"
                            data-testid="add-photos-label"
                          >
                            <ImagePlus className="w-6 h-6 mb-1" />
                            <span className="text-[10px] uppercase tracking-wider">
                              {newImages.length === 0 ? 'Add photos' : 'Add more'}
                            </span>
                            <input
                              type="file"
                              multiple
                              accept="image/*"
                              onChange={handleImagesSelected}
                              className="hidden"
                              data-testid="listing-photo-input"
                            />
                          </label>
                        )}
                      </div>
                      <p className="text-gray-500 text-xs mt-2">
                        {newImages.length}/{MAX_IMAGES} selected.
                        {newImages.length > 0 && newImages.length < 5 && ' Add a few more for a faster sale.'}
                      </p>
                    </div>

                    <Button 
                      type="submit" 
                      disabled={submitting}
                      className="w-full bg-[#FF3B30] hover:bg-[#E6352B]"
                      data-testid="submit-listing-btn"
                    >
                      {submitting
                        ? uploadProgress
                          ? `Uploading photos ${uploadProgress.done}/${uploadProgress.total}…`
                          : 'Creating...'
                        : `Create Listing${newImages.length > 0 ? ` + Upload ${newImages.length} Photo${newImages.length > 1 ? 's' : ''}` : ''}`}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            ) : (
              <Link to="/register">
                <Button className="bg-[#FF3B30] hover:bg-[#E6352B] text-white" data-testid="register-to-sell-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Register to Sell
                </Button>
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Filters Bar */}
      <div className="bg-[#141414] border-b border-[#262626]">
        <div className="container mx-auto px-4 py-4">
          <div className="flex flex-wrap items-center gap-4">
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className="border-[#262626] text-white"
              data-testid="toggle-filters-btn"
            >
              <Filter className="w-4 h-4 mr-2" />
              Filters
            </Button>
            
            {showFilters && (
              <div className="w-full flex flex-wrap gap-4 pt-4 border-t border-[#262626] mt-2">
                <div className="flex items-center gap-2">
                  <Label className="text-gray-400 text-sm">Price:</Label>
                  <Input
                    type="number"
                    placeholder="Min"
                    value={filters.min_price}
                    onChange={(e) => setFilters({ ...filters, min_price: e.target.value })}
                    className="w-24 bg-[#1F1F1F] border-[#262626] text-white"
                    data-testid="filter-min-price"
                  />
                  <span className="text-gray-500">-</span>
                  <Input
                    type="number"
                    placeholder="Max"
                    value={filters.max_price}
                    onChange={(e) => setFilters({ ...filters, max_price: e.target.value })}
                    className="w-24 bg-[#1F1F1F] border-[#262626] text-white"
                    data-testid="filter-max-price"
                  />
                </div>
                
                <div className="flex items-center gap-2">
                  <Label className="text-gray-400 text-sm">Year:</Label>
                  <Input
                    type="number"
                    placeholder="Min"
                    value={filters.year_min}
                    onChange={(e) => setFilters({ ...filters, year_min: e.target.value })}
                    className="w-20 bg-[#1F1F1F] border-[#262626] text-white"
                    data-testid="filter-year-min"
                  />
                  <span className="text-gray-500">-</span>
                  <Input
                    type="number"
                    placeholder="Max"
                    value={filters.year_max}
                    onChange={(e) => setFilters({ ...filters, year_max: e.target.value })}
                    className="w-20 bg-[#1F1F1F] border-[#262626] text-white"
                    data-testid="filter-year-max"
                  />
                </div>

                <div className="flex items-center gap-2">
                  <Label className="text-gray-400 text-sm">Location:</Label>
                  <Input
                    placeholder="State or city"
                    value={filters.location}
                    onChange={(e) => setFilters({ ...filters, location: e.target.value })}
                    className="w-32 bg-[#1F1F1F] border-[#262626] text-white"
                    data-testid="filter-location"
                  />
                </div>

                <Button onClick={handleApplyFilters} className="bg-[#FF3B30] hover:bg-[#E6352B]" data-testid="apply-filters-btn">
                  Apply
                </Button>
                <Button variant="ghost" onClick={handleClearFilters} className="text-gray-400" data-testid="clear-filters-btn">
                  Clear
                </Button>
              </div>
            )}

            <span className="text-gray-500 text-sm ml-auto">{listings.length} cars found</span>
          </div>
        </div>
      </div>

      {/* Listings Grid */}
      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {listings.map((listing) => (
            <div key={listing.id} className="relative">
              {user?.role === 'admin' && (
                <button
                  onClick={(e) => handleDeleteListing(e, listing)}
                  className="absolute top-3 left-3 z-10 w-9 h-9 flex items-center justify-center rounded bg-black/70 border border-[#262626] text-red-400 hover:bg-red-500 hover:text-white transition-colors"
                  title="Delete listing (admin)"
                  aria-label={`Delete listing ${listing.title}`}
                  data-testid={`admin-delete-listing-${listing.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
              <Link to={`/marketplace/${listing.id}`} data-testid={`listing-card-${listing.id}`}>
              <Card className="bg-[#141414] border-[#262626] overflow-hidden card-hover h-full flex flex-col">
                <div className="relative h-48">
                  <img
                    src={listing.images?.[0] ? getFileUrl(listing.images[0]) : getCrownVicImage(listing.id)}
                    alt={listing.title}
                    className="w-full h-full object-cover"
                  />
                  <span className="absolute top-3 right-3 tag bg-[#FF3B30] text-white px-2 py-1 rounded-sm">
                    For Sale
                  </span>
                </div>
                <div className="p-4 flex-1 flex flex-col">
                  <h3 className="font-heading font-bold text-lg text-white mb-2 line-clamp-2">{listing.title}</h3>
                  
                  <div className="flex items-center gap-4 mb-3">
                    <span className="text-[#FF3B30] font-bold text-xl flex items-center">
                      <DollarSign className="w-5 h-5" />
                      {formatPrice(listing.price).replace('$', '')}
                    </span>
                  </div>

                  <div className="space-y-1 text-sm text-gray-400 mt-auto">
                    <p className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      {listing.year} Crown Victoria
                    </p>
                    <p className="flex items-center gap-2">
                      <Gauge className="w-4 h-4" />
                      {formatMileage(listing.mileage)}
                    </p>
                    <p className="flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      {listing.location}
                    </p>
                  </div>
                </div>
              </Card>
            </Link>
            </div>
          ))}
        </div>

        {listings.length === 0 && (
          <div className="text-center py-16">
            <Car className="w-16 h-16 mx-auto text-gray-600 mb-4" />
            <p className="text-gray-400 text-lg mb-4">No cars found matching your criteria.</p>
            {user ? (
              <Button onClick={() => setIsNewListingOpen(true)} className="bg-[#FF3B30] hover:bg-[#E6352B]">
                Be the first to list a car
              </Button>
            ) : (
              <Link to="/login">
                <Button className="bg-[#FF3B30] hover:bg-[#E6352B]">
                  Login to list your car
                </Button>
              </Link>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
